import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { AppId, Bounds, Geometry, Project, WindowState } from './types'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BootScreen, BOOT_MS } from './boot-screen'
import { DragGhost } from './drag-ghost'
import { DesktopIcon, ICON_TILE_H, ICON_TILE_W } from './desktop-icon'
import { Taskbar, TASKBAR_H } from './taskbar'
import { WindowFrame } from './window-frame'
import { Desk95ThemeProvider, useDesk95Theme } from './theme'
import { ICON_LAYOUT, ICONS, PROJECTS, projectById } from './data'
import { useDragGhost } from './use-drag-ghost'
import { useIsMobile } from './use-is-mobile'
import { useWindows } from './use-windows'

import { MyComputer } from './apps/my-computer'
import { Notepad } from './apps/notepad'
import { Photoshop } from './apps/photoshop'
import { ProjectViewer } from './apps/project-viewer'

type Point = { x: number; y: number }

const MENUS: Record<AppId, string[]> = {
    photoshop: ['File', 'Edit', 'Image', 'Layer', 'Window', 'Help'],
    'my-computer': ['File', 'Edit', 'View', 'Help'],
    notepad: [],
    project: []
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max))

const Sim = (): ReactNode => {
    const palette = useDesk95Theme()
    const isMobile = useIsMobile()

    const desktopRef = useRef<HTMLDivElement>(null)
    const boundsRef = useRef<Bounds>({ w: 0, h: 0 })
    const seededRef = useRef(false)
    const autoOpenRef = useRef(false)

    const [bounds, setBounds] = useState<Bounds>({ w: 0, h: 0 })
    const [booted, setBooted] = useState(false)
    const [iconPositions, setIconPositions] = useState<Record<string, Point>>({})
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null)
    const [startOpen, setStartOpen] = useState(false)
    const [clock, setClock] = useState('')

    const {
        windows,
        activeUid,
        openWindow,
        focusWindow,
        closeWindow,
        toggleMinimize,
        moveWindow,
        resizeWindow
    } = useWindows()

    const { ghostRef, beginDrag } = useDragGhost(boundsRef, {
        onMoveWindow: moveWindow,
        onMoveIcon: (uid, x, y) => setIconPositions(prev => ({ ...prev, [uid]: { x, y } })),
        onResizeWindow: resizeWindow
    })

    useEffect(() => {
        const element = desktopRef.current

        if (!element) return

        const read = () => {
            const rect = element.getBoundingClientRect()
            const next = { w: Math.round(rect.width), h: Math.round(rect.height) }

            boundsRef.current = next
            setBounds(prev => (prev.w === next.w && prev.h === next.h ? prev : next))
        }

        read()

        const observer = new ResizeObserver(read)

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setBooted(true)

            return
        }

        const timer = window.setTimeout(() => setBooted(true), BOOT_MS)

        return () => window.clearTimeout(timer)
    }, [])

    useEffect(() => {
        const tick = () => setClock(new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))

        tick()

        const id = window.setInterval(tick, 15000)

        return () => window.clearInterval(id)
    }, [])

    useEffect(() => {
        if (seededRef.current || bounds.w === 0) return

        seededRef.current = true

        const seeded: Record<string, Point> = {}
        const roomy = bounds.w >= 560
        const rows = Math.max(1, Math.floor((bounds.h - 8) / (ICON_TILE_H + 8)))

        ICON_LAYOUT.forEach((spec, index) => {
            // Wide desktops use the authored percentage columns; narrow ones flow
            // the same order into columns so tiles never overlap.
            const x = roomy
                ? Math.round((bounds.w * spec.xPct) / 100)
                : 8 + Math.floor(index / rows) * (ICON_TILE_W + 12)
            const y = roomy
                ? Math.round((bounds.h * spec.yPct) / 100)
                : 8 + (index % rows) * (ICON_TILE_H + 8)

            seeded[spec.uid] = {
                x: clamp(x, 4, bounds.w - ICON_TILE_W - 4),
                y: clamp(y, 4, bounds.h - ICON_TILE_H - 4)
            }
        })

        setIconPositions(seeded)
    }, [bounds])

    const place = useCallback((natural: { w: number; h: number }, index: number): Geometry => {
        const area = boundsRef.current

        if (isMobile) {
            const w = Math.round(area.w * 0.92)
            const h = Math.round(area.h * 0.8)

            return {
                w,
                h,
                x: Math.round((area.w - w) / 2),
                y: Math.round((area.h - h) / 2)
            }
        }

        const w = Math.min(natural.w, Math.max(260, area.w - 60))
        const h = Math.min(natural.h, Math.max(180, area.h - 60))
        // The first window (About Me) lands dead centre, the rest cascade.
        const jitter = (index % 5) * 26

        return {
            w,
            h,
            x: clamp(Math.round((area.w - w) / 2) + jitter, 8, area.w - w - 8),
            y: clamp(Math.round((area.h - h) / 2) + jitter, 8, area.h - h - 8)
        }
    }, [isMobile])

    const openApp = useCallback((app: AppId) => {
        setStartOpen(false)

        if (app === 'photoshop') {
            openWindow({ app, title: 'Adobe Photoshop', icon: ICONS.photoshop, natural: { w: 580, h: 440 } }, place)

            return
        }

        if (app === 'my-computer') {
            openWindow({ app, title: 'My Computer', icon: ICONS.myComputer, natural: { w: 440, h: 320 } }, place)

            return
        }

        openWindow({ app: 'notepad', title: 'About.txt - Notepad', icon: ICONS.notepad, natural: { w: 640, h: 462 } }, place)
    }, [openWindow, place])

    const openProject = useCallback((project: Project) => {
        openWindow(
            {
                app: 'project',
                projectId: project.id,
                title: project.file,
                icon: ICONS.photoshop,
                natural: { w: 780, h: 640 }
            },
            place
        )
    }, [openWindow, place])

    useEffect(() => {
        if (!booted || bounds.w === 0 || autoOpenRef.current) return

        autoOpenRef.current = true

        openApp('notepad')
    }, [booted, bounds.w, openApp])

    const startWindowDrag = (event: ReactPointerEvent, win: WindowState) => {
        focusWindow(win.uid)
        beginDrag(event, { kind: 'window', uid: win.uid, origin: { x: win.x, y: win.y }, width: win.w, height: win.h })
    }

    const startResize = (event: ReactPointerEvent, win: WindowState) => {
        event.stopPropagation()
        focusWindow(win.uid)
        beginDrag(event, { kind: 'resize', uid: win.uid, origin: { x: win.x, y: win.y }, width: win.w, height: win.h })
    }

    const startIconDrag = (event: ReactPointerEvent, uid: string, position: Point) => {
        beginDrag(event, { kind: 'icon', uid, origin: position, width: ICON_TILE_W, height: ICON_TILE_H })
    }

    const openIcon = (uid: string, projectId?: string) => {
        if (projectId) {
            const project = projectById(projectId)

            if (project) openProject(project)

            return
        }

        if (uid === 'icon:photoshop') openApp('photoshop')
        else if (uid === 'icon:my-computer') openApp('my-computer')
        else openApp('notepad')
    }

    const body = (win: WindowState): ReactNode => {
        if (win.app === 'photoshop') {
            return <Photoshop isMobile={isMobile} onOpenProject={openProject} />
        }

        if (win.app === 'my-computer') {
            return <MyComputer isMobile={isMobile} onOpenProjects={() => openApp('photoshop')} />
        }

        if (win.app === 'notepad') {
            return <Notepad />
        }

        const project = win.projectId ? projectById(win.projectId) : undefined

        return project ? <ProjectViewer project={project} /> : null
    }

    const taskbarWindows = useMemo(() => [...windows].sort((a, b) => a.uid.localeCompare(b.uid)), [windows])

    return (
        <section
            id='desk-95'
            className='desk95 relative isolate w-full overflow-hidden'
            style={{
                height: '100svh',
                minHeight: 520,
                background: palette.desktop,
                color: palette.text,
                cursor: 'default'
            }}
            onPointerDown={() => setStartOpen(false)}
            aria-label='Desk 95, a Windows 95 desktop holding the studio archive'
        >
            <div
                ref={desktopRef}
                className='absolute inset-x-0 top-0'
                style={{
                    bottom: TASKBAR_H,
                    opacity: booted ? 1 : 0,
                    animation: booted ? 'desk95-desktop-in 300ms ease-out both' : undefined
                }}
            >
                <div
                    className='absolute inset-0'
                    onPointerDown={() => setSelectedIcon(null)}
                    aria-hidden='true'
                />

                {ICON_LAYOUT.map((spec, index) => {
                    const position = iconPositions[spec.uid]

                    if (!position) return null

                    return (
                        <DesktopIcon
                            key={spec.uid}
                            spec={spec}
                            project={spec.projectId ? projectById(spec.projectId) : undefined}
                            x={position.x}
                            y={position.y}
                            z={index + 1}
                            selected={selectedIcon === spec.uid}
                            tapToOpen={isMobile}
                            onSelect={() => setSelectedIcon(spec.uid)}
                            onOpen={() => openIcon(spec.uid, spec.projectId)}
                            onPointerDown={event => startIconDrag(event, spec.uid, position)}
                        />
                    )
                })}

                {windows.filter(win => !win.minimized).map(win => (
                    <WindowFrame
                        key={win.uid}
                        win={win}
                        active={win.uid === activeUid}
                        menu={MENUS[win.app]}
                        chrome={win.app === 'project' ? 'sheet' : 'classic'}
                        onFocus={() => focusWindow(win.uid)}
                        onClose={() => closeWindow(win.uid)}
                        onMinimize={() => toggleMinimize(win.uid)}
                        onTitlePointerDown={event => startWindowDrag(event, win)}
                        onGripPointerDown={event => startResize(event, win)}
                    >
                        {body(win)}
                    </WindowFrame>
                ))}

                <DragGhost ghostRef={ghostRef} />
            </div>

            <Taskbar
                windows={taskbarWindows}
                activeUid={activeUid}
                compact={isMobile}
                clock={clock}
                startOpen={startOpen}
                onToggleStart={() => setStartOpen(open => !open)}
                onLaunch={openApp}
                onSelectWindow={uid => {
                    const target = windows.find(item => item.uid === uid)

                    if (!target) return

                    if (target.minimized || target.uid !== activeUid) {
                        if (target.minimized) toggleMinimize(uid)
                        else focusWindow(uid)
                    } else {
                        toggleMinimize(uid)
                    }
                }}
            />

            {!booted && <BootScreen />}

            <p className='sr-only'>
                {PROJECTS.length} archive documents are on this desktop: {PROJECTS.map(project => project.title).join(', ')}.
            </p>
        </section>
    )
}

export const Desk95 = (): ReactNode => {
    return (
        <Desk95ThemeProvider>
            <Sim />
        </Desk95ThemeProvider>
    )
}
